import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy, where, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';
import { FaSearch, FaEnvelope, FaUser, FaShoppingBag, FaTimes } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: #333;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-weight: 600;
  margin-right: 1rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled.div`
  font-weight: 500;
`;

const UserEmail = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: #e2f0ff;
  color: #0d6efd;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #cfe2ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const EmptyTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: #666;
  margin-bottom: 1rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  color: #666;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const OrderId = styled.div`
  font-weight: 600;
  color: #333;
`;

const OrderStatus = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch(props.status) {
      case 'completed': return '#d4edda';
      case 'shipped': return '#cfe2ff';
      case 'delivered': return '#d1e7dd';
      case 'pending': return '#fff3cd';
      default: return '#f8d7da';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'completed': return '#155724';
      case 'shipped': return '#084298';
      case 'delivered': return '#0f5132';
      case 'pending': return '#856404';
      default: return '#721c24';
    }
  }};
`;

const OrderTotal = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
`;

const ViewOrderButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #0d6efd;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: #0b5ed7;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const SendButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  
  &:hover {
    background-color: #a01830;
  }
`;

const CustomerManagement = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const customersPerPage = 10;
  
  useEffect(() => {
    fetchCustomers();
  }, []);
  
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      
      // First, get all orders to extract unique customers
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'), limit(50));
      const ordersSnapshot = await getDocs(ordersQuery);
      
      // Extract unique customers from orders
      const uniqueCustomers = new Map();
      
      ordersSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.customerInfo && data.customerInfo.email) {
          const email = data.customerInfo.email;
          
          // Only count paid orders (status: 'completed' or 'shipped')
          const isPaid = data.status === 'completed' || data.status === 'shipped' || data.status === 'delivered';
          const orderTotal = isPaid ? (data.orderDetails?.total || data.total || 0) : 0;
          
          if (!uniqueCustomers.has(email)) {
            uniqueCustomers.set(email, {
              id: data.customerInfo.userId || 'guest-' + Math.random().toString(36).substring(2, 10),
              name: data.customerInfo.name || 'Guest',
              email: email,
              phone: data.customerInfo.phone || 'N/A',
              totalSpent: orderTotal,
              totalOrders: isPaid ? 1 : 0,
              lastOrder: data.createdAt,
              isAdmin: false,
              orders: [{ id: doc.id, status: data.status, total: data.orderDetails?.total || data.total || 0 }]
            });
          } else {
            // Update existing customer data
            const customer = uniqueCustomers.get(email);
            
            customer.totalSpent += orderTotal;
            if (isPaid) {
              customer.totalOrders += 1;
            }
            
            // Update last order date if this order is more recent
            if (data.createdAt && (!customer.lastOrder || data.createdAt > customer.lastOrder)) {
              customer.lastOrder = data.createdAt;
            }
            
            // Add order to customer's orders
            customer.orders.push({ id: doc.id, status: data.status, total: data.orderDetails?.total || data.total || 0 });
            
            uniqueCustomers.set(email, customer);
          }
        }
      });
      
      // Convert Map to array
      const customersList = Array.from(uniqueCustomers.values());
      
      // Sort by total spent (descending)
      customersList.sort((a, b) => b.totalSpent - a.totalSpent);
      
      setCustomers(customersList);
      setHasMore(false); // Since we're processing all customers at once for now
      
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchQuery.toLowerCase();
    return (
      customer.name.toLowerCase().includes(searchLower) ||
      customer.email.toLowerCase().includes(searchLower) ||
      customer.phone.toLowerCase().includes(searchLower)
    );
  });
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name || name === 'Guest') return 'G';
    
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };
  
  // Handle view customer orders
  const handleViewOrders = (customer) => {
    setSelectedCustomer(customer);
    setShowOrdersModal(true);
  };
  
  // Handle email customer
  const handleEmailCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEmailSubject('');
    setEmailMessage('');
    setShowEmailModal(true);
  };
  
  // Send email to customer
  const sendEmail = () => {
    if (!selectedCustomer || !emailSubject || !emailMessage) {
      alert('Please fill in subject and message');
      return;
    }
    
    // Create mailto link
    const mailtoLink = `mailto:${selectedCustomer.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailMessage)}`;
    window.location.href = mailtoLink;
    
    setShowEmailModal(false);
  };
  
  return (
    <Container>
      <AdminNav />
      
      <Header>
        <Title>Customer Management</Title>
        <Subtitle>View and manage customer information</Subtitle>
      </Header>
      
      <SearchContainer>
        <SearchIcon>
          <FaSearch />
        </SearchIcon>
        <SearchInput 
          type="text" 
          placeholder="Search customers by name, email, or phone..." 
          value={searchQuery}
          onChange={handleSearch}
        />
      </SearchContainer>
      
      {loading && customers.length === 0 ? (
        <EmptyState>
          <EmptyTitle>Loading customers...</EmptyTitle>
        </EmptyState>
      ) : filteredCustomers.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>Phone</TableHeader>
                <TableHeader>Total Spent</TableHeader>
                <TableHeader>Orders</TableHeader>
                <TableHeader>Last Order</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {filteredCustomers.map(customer => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <UserInfo>
                      <UserAvatar>
                        {getInitials(customer.name)}
                      </UserAvatar>
                      <UserName>{customer.name}</UserName>
                    </UserInfo>
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>{formatCurrency(customer.totalSpent)}</TableCell>
                  <TableCell>{customer.totalOrders}</TableCell>
                  <TableCell>{formatDate(customer.lastOrder)}</TableCell>
                  <TableCell>
                    <ActionButtons>
                      <IconButton title="View Customer Orders" onClick={() => handleViewOrders(customer)}>
                        <FaShoppingBag />
                      </IconButton>
                      <IconButton title="Email Customer" onClick={() => handleEmailCustomer(customer)}>
                        <FaEnvelope />
                      </IconButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <EmptyState>
          <EmptyTitle>No customers found</EmptyTitle>
          <EmptyText>
            {searchQuery 
              ? `No customers match your search for "${searchQuery}"`
              : "There are no customers in the system yet."}
          </EmptyText>
        </EmptyState>
      )}
      
      {/* Orders Modal */}
      {showOrdersModal && selectedCustomer && (
        <Modal onClick={() => setShowOrdersModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Orders for {selectedCustomer.name}</ModalTitle>
              <CloseButton onClick={() => setShowOrdersModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {selectedCustomer.orders && selectedCustomer.orders.length > 0 ? (
                <OrdersList>
                  {selectedCustomer.orders.map((order, index) => (
                    <OrderItem key={index}>
                      <OrderInfo>
                        <OrderId>Order #{order.id.substring(0, 8)}</OrderId>
                        <OrderStatus status={order.status}>
                          {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                        </OrderStatus>
                      </OrderInfo>
                      <OrderTotal>{formatCurrency(order.total)}</OrderTotal>
                      <ViewOrderButton onClick={() => {
                        setShowOrdersModal(false);
                        navigate(`/admin/orders`);
                      }}>
                        View Details
                      </ViewOrderButton>
                    </OrderItem>
                  ))}
                </OrdersList>
              ) : (
                <EmptyText>No orders found</EmptyText>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      
      {/* Email Modal */}
      {showEmailModal && selectedCustomer && (
        <Modal onClick={() => setShowEmailModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Email {selectedCustomer.name}</ModalTitle>
              <CloseButton onClick={() => setShowEmailModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>To:</Label>
                <Input type="text" value={selectedCustomer.email} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Subject:</Label>
                <Input 
                  type="text" 
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                />
              </FormGroup>
              <FormGroup>
                <Label>Message:</Label>
                <TextArea 
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Enter your message"
                  rows={6}
                />
              </FormGroup>
              <SendButton onClick={sendEmail}>Send Email</SendButton>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default CustomerManagement;
